<template>
  <v-row no-gutters align="center">
    <v-col cols="12" class="pa-0">
      <div v-if="!suppliersList.length" class="d-flex justify-center align-center" style="width: 100%; min-height: calc(100vh - 300px);">
        <v-empty-state type="suppliers" id="document" :height="180" customClass="mt-6" :hiddenDescription="!emptyDescription" :isFree="true" :title="emptyTitle" :description="emptyDescription">
          <template v-slot:actions>
            <slot name="empty-actions"></slot>
          </template>
        </v-empty-state>
      </div>
      <template v-else>
        <v-simple-table :height="heightTable" fixed-header :class="[{'fixed-table' : $store.state.base.isExpandNavigationDrawer}]" :key="reset">
          <template v-slot:default>
            <VTableHeaders :headers="headers" @sort="setSort" />
            <v-divider style="position: absolute; margin-top: -6px; margin-left: -5px; z-index: 2; min-width: 100%" />
            <tbody :key="resetScroll">
              <tr
                v-for="(item, i) in suppliersList"
                :key="i"
                v-intersect="useInfiniteScroll && (i + 1) === suppliersList?.length ? onLoadMore : ''"
                @click="goToSupplier(item)"
                style="cursor: pointer"
              >
                <td class="body-2">
                  <template v-if="item.name || item.tax_id">
                    <span class="body-1 d-inline-block text-truncate" style="max-width: 95%;" v-if="item.name">{{ item.name }}</span>
                    <span class="d-block body-2 text--disabled font-italic" v-else>Sin especificar</span>
                    <span class="mt-n1 caption grey-300--text d-block" v-if="item.tax_id">{{$t('generals.RUT')}} {{item.tax_id}}</span>
                  </template>
                  <span class="body-2 text--disabled font-italic" v-else>Sin especificar</span>
                </td>
                <td class="body-2">
                  <span class="d-inline-block text-truncate" style="max-width: 96%;" v-if="item.tax_settings?.cl_activity_description || item.business_activity">{{item.tax_settings?.cl_activity_description || item.business_activity}}</span>
                  <span class="body-2 text--disabled" v-else>Sin especificar</span>
                </td>
                <td class="body-2" v-if="showCategoryColumn">
                  <span class="d-inline-block text-truncate" style="max-width: 96%;" v-if="getCategoryName(item) && getCategoryName(item) !== 'Bloqueados' && !isDefaultSegment(item.segment_id)">{{ getCategoryName(item) }}</span>
                  <v-chip v-else-if="getCategoryName(item) === 'Bloqueados'" color="greyMedium" class="font-weight-semibold" small label>Bloqueados<v-icon right>mdi-lock</v-icon></v-chip>
                  <span class="body-2 text--disabled" v-else>Sin categoría asociada</span>
                </td>
                <td class="body-2 text-end">{{item.created | dateTimeSecond}}</td>
                <td class="body-2 text-end" v-if="showAssignedDateColumn">
                  <span v-if="item.group_assigned_date">{{item.group_assigned_date | dateTimeSecond}}</span>
                  <span class="body-2 text--disabled" v-else>Sin especificar</span>
                </td>
                <td class="text-end">
                  <v-menu close-on-click close-on-content-click offset-y left :nudge-bottom="10" :nudge-left="10" transition="slide-y-transition">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn class="mr-n4" v-bind="attrs" v-on="on" text x-small :disabled="getCategoryName(item) === 'Bloqueados'" @click.stop><v-icon color="grey-500" size="24">mdi-dots-vertical</v-icon></v-btn>
                    </template>
                    <v-list class="pa-0">
                      <!-- Eliminar: solo si tiene segment_id diferente al default -->
                      <v-list-item class="px-3" @click.stop="$emit('delete', item)" v-if="item.segment_id && item.segment_id.length > 0 && !isDefaultSegment(item.segment_id)" :ripple="false">
                        <v-list-item-title class="body-2 d-flex align-center">
                          <v-icon color="grey-500" size="16" left>mdi-folder-minus</v-icon>Eliminar de la categoria
                        </v-list-item-title>
                      </v-list-item>
                      <!-- Agregar: solo si NO tiene segment_id o es el default -->
                      <v-list-item class="px-3" @click.stop="$emit('add', item)" v-if="!item.segment_id || item.segment_id.length === 0 || isDefaultSegment(item.segment_id)" :ripple="false">
                        <v-list-item-title class="body-2 d-flex align-center">
                          <v-icon color="grey-500" size="16" left>mdi-folder-plus</v-icon>Agregar a categoria
                        </v-list-item-title>
                      </v-list-item>
                      <!-- Cambiar: solo si tiene segment_id diferente al default -->
                      <v-list-item class="px-3" @click.stop="$emit('change', item)" v-if="item.segment_id && item.segment_id.length > 0 && !isDefaultSegment(item.segment_id)" :ripple="false">
                        <v-list-item-title class="body-2 d-flex align-center">
                          <v-icon color="grey-500" size="16" left>mdi-swap-horizontal</v-icon>Cambiar de categoria
                        </v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                </td>
              </tr>
            </tbody>
          </template>
        </v-simple-table>
      </template>
    </v-col>
  </v-row>
</template>

<script>
import VEmptyState from '@/components/commons/VEmptyState'
import VTableHeaders from '@/components/VTable/VTableHeaders'
import ListViewMixin from '@/mixins/ListViewMixin'

export default {
  name: 'SupplierList',
  components: {
    VTableHeaders,
    VEmptyState
  },
  mixins: [
    ListViewMixin
  ],
  props: {
    suppliersList: {
      type: Array,
      default: () => []
    },
    reset: {
      type: Number,
      default: 0
    },
    resetScroll: {
      type: Number,
      default: 0
    },
    useInfiniteScroll: {
      type: Boolean,
      default: false
    },
    showCategoryColumn: {
      type: Boolean,
      default: true
    },
    showAssignedDateColumn: {
      type: Boolean,
      default: false
    },
    emptyTitle: {
      type: String,
      default: 'No existen proveedores para mostrar'
    },
    emptyDescription: {
      type: String,
      default: ''
    },
    hasMoreData: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      defaultSegmentId: 'se_AAAAAAAAAAAAAAAA'
    }
  },
  computed: {
    headers () {
      const baseHeaders = [
        { text: 'Proveedor', value: 'name', class: 'font-weight-bold', sortable: true },
        { text: 'Actividad económica', value: 'tax_settings', sortable: true }
      ]
      if (this.showCategoryColumn) {
        baseHeaders.push({ text: 'Categoría', value: 'segment_id', sortable: true })
      }
      baseHeaders.push({ text: 'Fecha de creación', value: 'created', align: 'end', sortable: true })
      if (this.showAssignedDateColumn) {
        baseHeaders.push({ text: 'Ingreso a categoría', value: 'group_assigned_date', align: 'end', sortable: true })
      }
      baseHeaders.push({ text: '', value: 'actions', align: 'end' })
      return baseHeaders
    },
    heightTable () {
      // Si hay más datos por cargar, siempre usar altura fija para que funcione el scroll
      if (this.hasMoreData) return 'calc(100vh - 275px)'
      // Si no hay más datos y hay pocos registros, adaptar al contenido
      if (this.suppliersList.length < 15) return 'auto'
      return 'calc(100vh - 275px)'
    },
    isDefaultSegment () {
      return (id) => id === this.defaultSegmentId
    }
  },
  methods: {
    goToSupplier (item) {
      this.$router.push({ name: 'SupplierRetrieveUpdate', params: { id: item.id } })
    },
    onLoadMore (entries, observer, isIntersecting) {
      if (isIntersecting) {
        this.$emit('load-more', { entries, observer, isIntersecting })
      }
    },
    getCategoryName (item) {
      return item.category_details?.name || item.group_details?.name || null
    }
  }
}
</script>
